// v10/src/components/admin/inventory/modals/DeleteInventoryItemModal.js
//
// Modal "Eliminar producto" del Stock de repuestos (A8) — Tarea 2.
// DELETE /api/inventory-items/:id (soft delete con is_active=false)
//
// 409 → producto con historial de movimientos: bloqueado, mensaje del backend
// con botón "Entendido" en lugar de "Eliminar".

import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, Trash2, AlertTriangle, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import inventoryService from '../../../../services/inventoryService';

const DeleteInventoryItemModal = ({ isOpen, item, onClose, onSuccess, darkMode }) => {
  const { t } = useTranslation('admin');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [blockedMessage, setBlockedMessage] = useState('');

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  useEffect(() => {
    if (isOpen) {
      setError('');
      setBlockedMessage('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, submitting]);

  const handleDelete = async () => {
    if (!item?.id || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await inventoryService.delete(item.id);
      if (!isMounted.current) return;
      onSuccess?.(item.id);
      onClose?.();
    } catch (err) {
      if (!isMounted.current) return;
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      if (status === 409) {
        setBlockedMessage(message || t('inventory_modal_delete_warning'));
      } else {
        setError(message || t('inventory_modal_network_error'));
      }
    } finally {
      if (isMounted.current) setSubmitting(false);
    }
  };

  if (!isOpen || !item) return null;

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';

  const isBlocked = !!blockedMessage;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={submitting ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 480, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: -8 }}>
          <button
            type="button" onClick={onClose} disabled={submitting}
            aria-label={t('inventory_modal_close')}
            style={{
              padding: 6, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: submitting ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: isBlocked ? 'rgba(245,158,11,0.10)' : 'rgba(239,68,68,0.1)',
            border: isBlocked ? '2px solid rgba(245,158,11,0.25)' : '2px solid rgba(239,68,68,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            {isBlocked
              ? <Lock style={{ width: 26, height: 26, color: '#d97706' }} />
              : <AlertTriangle style={{ width: 26, height: 26, color: '#ef4444' }} />}
          </div>
          <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: fgPrimary }}>
            {isBlocked ? t('inventory_modal_delete_blocked_title') : t('inventory_modal_delete_title')}
          </h3>
          {isBlocked ? (
            <p style={{ margin: 0, fontSize: 13.5, color: fgMuted, lineHeight: 1.5 }}>
              {blockedMessage}
            </p>
          ) : (
            <>
              <p style={{ margin: 0, fontSize: 14, color: fgMuted, lineHeight: 1.5 }}>
                {t('inventory_modal_delete_confirm', { name: item.name })}
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: fgMuted, lineHeight: 1.5 }}>
                {t('inventory_modal_delete_warning')}
              </p>
            </>
          )}
        </div>

        {error && !isBlocked && (
          <div style={{
            padding: '10px 14px', borderRadius: 12,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
            marginBottom: 12,
          }}>
            <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          {isBlocked ? (
            <button
              type="button" onClick={onClose}
              style={{
                flex: 1, padding: 13, borderRadius: 14, border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
              }}
            >
              {t('inventory_modal_btn_understood')}
            </button>
          ) : (
            <>
              <button
                type="button" onClick={onClose} disabled={submitting}
                style={{
                  flex: 1, padding: 13, borderRadius: 14,
                  border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                  background: 'transparent',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: 14, color: fgMuted, fontFamily: 'inherit',
                }}
              >
                {t('inventory_modal_btn_cancel')}
              </button>
              <motion.button
                type="button" onClick={handleDelete} disabled={submitting}
                whileHover={!submitting ? { scale: 1.02 } : {}}
                whileTap={!submitting ? { scale: 0.97 } : {}}
                style={{
                  flex: 1, padding: 13, borderRadius: 14, border: 'none',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  background: 'linear-gradient(135deg,#dc2626,#ef4444)',
                  color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
                {submitting
                  ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                  : <Trash2 style={{ width: 16, height: 16 }} />
                }
                {submitting
                  ? t('inventory_modal_delete_action_loading')
                  : t('inventory_modal_delete_action')}
              </motion.button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DeleteInventoryItemModal;
